/*
  Communite global header
  Accessible mega navigation and mobile drawer with Shopify editor lifecycle support.
*/

(() => {
  const ROOT_SELECTOR = '[data-cm-header]'
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',')
  const controllers = new WeakMap()

  const findRoots = (root) => {
    const roots = []

    if (root instanceof Element && root.matches(ROOT_SELECTOR)) {
      roots.push(root)
    }

    root.querySelectorAll?.(ROOT_SELECTOR).forEach(element => roots.push(element))
    return roots
  }

  const getFocusableElements = (element) => {
    return [...element.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter(control => !control.hidden && control.getAttribute('aria-hidden') !== 'true')
  }

  let lockedScrollY = 0

  /*
    Drawer açılırken sayfa kaydırması kilitlenir. Kök elementte
    overflow: hidden iOS Safari'de yetmediği için body sabitlenir;
    kaydırma konumu --cm-locked-scroll ile görsel olarak korunur ve
    kapanışta geri yüklenir.
  */
  const syncDocumentLock = () => {
    const hasOpenMenu = Boolean(document.querySelector('[data-cm-mobile-menu]:not([hidden])'))
    const root = document.documentElement

    if (hasOpenMenu === root.classList.contains('cm-mobile-nav-is-open')) return

    if (hasOpenMenu) {
      lockedScrollY = window.scrollY
      root.style.setProperty('--cm-locked-scroll', `-${lockedScrollY}px`)
      root.classList.add('cm-mobile-nav-is-open')
      return
    }

    root.classList.remove('cm-mobile-nav-is-open')
    root.style.removeProperty('--cm-locked-scroll')
    // Kök elementte scroll-behavior: smooth var; geri dönüş anlık olmalı.
    window.scrollTo({ top: lockedScrollY, behavior: 'instant' })
  }

  class CommuniteHeaderController {
    constructor (root) {
      this.root = root
      this.abortController = new AbortController()
      this.signal = this.abortController.signal
      this.mobileMenu = root.querySelector('[data-cm-mobile-menu]')
      this.mobileTrigger = root.querySelector('[data-cm-mobile-open]')
      this.backdrop = root.querySelector('[data-cm-header-backdrop]')
      this.activeMegaTrigger = null
      this.activeMegaPanel = null
      this.mobileOpen = false

      this.root.addEventListener('click', event => this.handleClick(event), { signal: this.signal })
      document.addEventListener('keydown', event => this.handleKeydown(event), { signal: this.signal })
      document.addEventListener('pointerdown', event => this.handlePointerDown(event), { signal: this.signal })
      document.addEventListener('cm:header:before-open', event => {
        if (event.detail?.controller !== this) this.closeAll(false)
      }, { signal: this.signal })
      window.addEventListener('resize', () => this.handleResize(), { signal: this.signal })
    }

    handleClick (event) {
      const mobileOpener = event.target.closest('[data-cm-mobile-open]')

      if (mobileOpener && this.root.contains(mobileOpener)) {
        event.preventDefault()
        this.openMobile()
        return
      }

      const mobileCloser = event.target.closest('[data-cm-mobile-close]')

      if (mobileCloser && this.root.contains(mobileCloser)) {
        event.preventDefault()
        this.closeMobile()
        return
      }

      if (event.target === this.backdrop) {
        this.closeMobile()
        return
      }

      const megaTrigger = event.target.closest('[data-cm-mega-trigger]')

      if (megaTrigger && this.root.contains(megaTrigger)) {
        event.preventDefault()
        this.toggleMega(megaTrigger)
        return
      }

      const mobileLink = event.target.closest('[data-cm-mobile-menu] a[href]')
      if (mobileLink && this.mobileOpen) this.closeMobile(false)
    }

    handlePointerDown (event) {
      if (!this.activeMegaPanel) return

      const activeItem = this.activeMegaTrigger?.closest('[data-cm-nav-item]')
      if (!activeItem?.contains(event.target)) this.closeMega()
    }

    handleKeydown (event) {
      if (event.key === 'Escape') {
        if (this.mobileOpen) {
          event.preventDefault()
          this.closeMobile()
        } else if (this.activeMegaPanel) {
          event.preventDefault()
          this.closeMega(true)
        }
        return
      }

      if (event.key !== 'Tab' || !this.mobileOpen || !this.mobileMenu) return

      const focusableElements = getFocusableElements(this.mobileMenu)

      if (!focusableElements.length) {
        event.preventDefault()
        this.mobileMenu.focus()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    handleResize () {
      if (window.innerWidth >= 1024 && this.mobileOpen) this.closeMobile(false)
    }

    openMobile () {
      if (!this.mobileMenu || this.mobileOpen) return

      document.dispatchEvent(new CustomEvent('cm:header:before-open', {
        detail: { controller: this }
      }))

      this.closeMega()
      this.mobileOpen = true
      this.mobileMenu.removeAttribute('hidden')
      this.mobileMenu.setAttribute('aria-hidden', 'false')
      this.backdrop?.removeAttribute('hidden')
      this.mobileTrigger?.setAttribute('aria-expanded', 'true')
      syncDocumentLock()

      const focusTarget = getFocusableElements(this.mobileMenu)[0] || this.mobileMenu
      window.requestAnimationFrame(() => focusTarget.focus())
    }

    closeMobile (restoreFocus = true) {
      if (!this.mobileMenu || !this.mobileOpen) return

      this.mobileOpen = false
      this.mobileMenu.setAttribute('hidden', 'hidden')
      this.mobileMenu.setAttribute('aria-hidden', 'true')
      this.backdrop?.setAttribute('hidden', 'hidden')
      this.mobileTrigger?.setAttribute('aria-expanded', 'false')
      syncDocumentLock()

      if (restoreFocus && this.mobileTrigger?.isConnected) {
        window.requestAnimationFrame(() => this.mobileTrigger.focus())
      }
    }

    toggleMega (trigger) {
      const panelId = trigger.dataset.cmMegaTrigger
      const panel = panelId ? this.root.querySelector(`#${CSS.escape(panelId)}`) : null

      if (!panel) return

      if (panel === this.activeMegaPanel) {
        this.closeMega()
        return
      }

      document.dispatchEvent(new CustomEvent('cm:header:before-open', {
        detail: { controller: this }
      }))

      this.closeMega()
      this.activeMegaTrigger = trigger
      this.activeMegaPanel = panel
      trigger.setAttribute('aria-expanded', 'true')
      panel.removeAttribute('hidden')
      panel.setAttribute('aria-hidden', 'false')
    }

    closeMega (restoreFocus = false) {
      if (!this.activeMegaPanel) return

      const trigger = this.activeMegaTrigger
      trigger?.setAttribute('aria-expanded', 'false')
      this.activeMegaPanel.setAttribute('hidden', 'hidden')
      this.activeMegaPanel.setAttribute('aria-hidden', 'true')
      this.activeMegaTrigger = null
      this.activeMegaPanel = null

      if (restoreFocus && trigger?.isConnected) {
        window.requestAnimationFrame(() => trigger.focus())
      }
    }

    closeAll (restoreFocus = false) {
      this.closeMobile(restoreFocus)
      this.closeMega(restoreFocus)
    }

    destroy () {
      this.closeAll(false)
      this.abortController.abort()
      controllers.delete(this.root)
    }
  }

  const init = (root = document) => {
    findRoots(root).forEach(element => {
      if (controllers.has(element)) return
      controllers.set(element, new CommuniteHeaderController(element))
    })
  }

  const destroy = (root) => {
    findRoots(root).forEach(element => controllers.get(element)?.destroy())
  }

  if (!window.CommuniteHeader) {
    window.CommuniteHeader = { init, destroy }

    document.addEventListener('shopify:section:load', event => init(event.target))
    document.addEventListener('shopify:section:unload', event => destroy(event.target))
    document.addEventListener('shopify:section:select', event => init(event.target))
    document.addEventListener('shopify:block:select', event => {
      const root = event.target.closest?.(ROOT_SELECTOR)
      if (root) init(root)
    })
  }

  window.CommuniteHeader.init(document)
})()
